import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

export type User = {
  user_id: string;
  email: string;
  name: string;
  picture?: string;
  phone?: string;
  city?: string;
  bio?: string;
  role: string;
  is_organizer: boolean;
  email_verified: boolean;
  phone_verified: boolean;
  badges: string[];
};

type Ctx = {
  user: User | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: () => void;
  logout: () => Promise<void>;
  setUser: (u: User | null) => void;
};

const AuthContext = createContext<Ctx>({} as Ctx);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: returning from OAuth callback -> AuthCallback exchanges the code/session first.
    if (window.location.hash?.includes("session_id=") || window.location.pathname === "/auth/google") {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    try {
      const { data } = await api.get("/auth/oauth-config");
      if (data.provider === "google_oauth") {
        const params = new URLSearchParams({
          client_id: data.client_id,
          redirect_uri: window.location.origin + "/auth/google",
          response_type: "code",
          scope: "openid email profile",
          prompt: "select_account",
        });
        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
        return;
      }
    } catch {
      // config endpoint unreachable -> fall through to Emergent flow
    }
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // session may already be gone
    }
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, loading, refresh, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
